import { useMutation, useQueryClient } from '@tanstack/react-query';
import { objectiveService } from '../../services/objectiveService';
import type { ObjectiveCreate, ObjectiveUpdate } from '../../models/objective.schemas';
import { useToast } from '@/hooks/useToast';

/**
 * Hook for objective mutations (create, update, archive)
 * Provides optimistic updates and cache invalidation
 *
 * @returns Mutation functions for objectives
 */
export function useObjectiveMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();

  /**
   * Create objective mutation
   */
  const createObjective = useMutation({
    mutationFn: (data: ObjectiveCreate) => objectiveService.create(data),
    onSuccess: (newId, variables) => {
      // Invalidate queries related to the cycle
      queryClient.invalidateQueries({ queryKey: ['objectives', variables.cycleId] });

      // Also invalidate parent objectives if applicable
      if (variables.parentObjectiveId) {
        queryClient.invalidateQueries({ queryKey: ['objectives'] });
      }
    },
    onError: (error: any) => {
      console.error('Error creating objective:', error);
      const message = error?.message || 'Erro desconhecido ao criar objetivo';
      toast.error(`Erro ao criar objetivo: ${message}`);
    },
  });

  /**
   * Update objective mutation with optimistic updates
   */
  const updateObjective = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ObjectiveUpdate }) =>
      objectiveService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['objectives'] });

      // Get previous data for rollback
      const previousObjectives = queryClient.getQueriesData({ queryKey: ['objectives'] });

      // Optimistically update cache
      queryClient.setQueriesData({ queryKey: ['objectives'] }, (old: any) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((obj: any) => (obj.id === id ? { ...obj, ...data } : obj));
      });

      return { previousObjectives };
    },
    onError: (error: any, variables, context) => {
      console.error('Error updating objective:', error);
      const message = error?.message || 'Erro desconhecido ao atualizar objetivo';
      toast.error(`Erro ao atualizar objetivo: ${message}`);

      // Rollback on error
      if (context?.previousObjectives) {
        context.previousObjectives.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Invalidate all objectives queries to refetch
      queryClient.invalidateQueries({ queryKey: ['objectives'] });
    },
  });

  /**
   * Archive objective mutation
   */
  const archiveObjective = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      objectiveService.archive(id, userId),
    onMutate: async ({ id }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['objectives'] });

      // Get previous data for rollback
      const previousObjectives = queryClient.getQueriesData({ queryKey: ['objectives'] });

      // Optimistically update cache - mark as cancelled
      queryClient.setQueriesData({ queryKey: ['objectives'] }, (old: any) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((obj: any) =>
          obj.id === id ? { ...obj, status: 'cancelled' } : obj
        );
      });

      return { previousObjectives };
    },
    onError: (error: any, variables, context) => {
      console.error('Error archiving objective:', error);
      const message = error?.message || 'Erro desconhecido ao arquivar objetivo';
      toast.error(`Erro ao arquivar objetivo: ${message}`);

      // Rollback on error
      if (context?.previousObjectives) {
        context.previousObjectives.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      // Invalidate all objectives queries
      queryClient.invalidateQueries({ queryKey: ['objectives'] });
    },
  });

  return {
    createObjective,
    updateObjective,
    archiveObjective,
  };
}
